import { useCallback, useEffect, useState } from 'react';

import { mensagemErro } from '../api/axios';
import {
  aplicarSeeds,
  limparBase,
  limparOrfaos,
  previaDaLimpeza,
  previaDeOrfaos,
  resumoDasSeeds,
} from '../api/fluxfin';
import {
  Alerta,
  Badge,
  Botao,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Painel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { numero } from '../utils/formato';

/* Nomes de tabela em linguagem de gente. O que a pessoa precisa reconhecer é
   o que vai sumir da tela dela, não o nome da tabela no PostgreSQL. */
const APELIDO = {
  arquivo_recebido: 'Arquivos recebidos',
  linha_extraida: 'Linhas extraídas dos arquivos',
  guia_pagamento: 'Guias com valor pago',
  glosa_item: 'Itens glosados',
  validacao_arquivo: 'Conferências aritméticas',
  pendencia: 'Pendências de conciliação',
  divergencia: 'Divergências cobrado × pago',
  resolucao_protocolo: 'Vínculos guia → protocolo',
  consulta_bioslab_log: 'Consultas ao Bioslab',
  saldo_protocolo: 'Saldos por protocolo',
  saldo_historico: 'Histórico de apuração do saldo',
  lote_envio: 'Envios em lote',
  item_lote: 'Itens dos envios em lote',
  operadora: 'Operadoras',
  modelo_arquivo: 'Modelos de leitura',
  conjunto_modelo: 'Conjuntos de modelos',
  usuario_portal: 'Usuários',
  audit_log: 'Auditoria',
};

const nomeDe = (t) => APELIDO[t] || t;

/* Tamanho em unidade legível. KB abaixo de 1 MB, senão "0,0 MB" para tudo
   que é pequeno não informa nada. */
const mb = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/* O que a seed vai fazer com cada modelo, dito ANTES do clique. "Aplicar
   seeds" sem isso é um botão que faz algo não nomeado. */
function destinoDaSeed(m) {
  if (m.erro) return { tom: 'reprovado', texto: 'JSON inválido — será ignorado' };
  if (!m.ja_existe) return { tom: 'confirmado', texto: 'Será criado' };
  if (m.igual)
    return { tom: 'arquivado', texto: `Idêntico à v${m.versao_atual} — nada muda` };
  if (m.ja_leu_arquivo > 0)
    return {
      tom: 'pendente',
      texto: `Nova versão; a v${m.versao_atual} fica inativa (já leu ${numero(
        m.ja_leu_arquivo
      )} arquivo(s))`,
    };
  return { tom: 'conferencia', texto: `Substitui a v${m.versao_atual}` };
}

export default function Controles() {
  const [previa, setPrevia] = useState(null);
  const [seeds, setSeeds] = useState(null);
  const [orfaos, setOrfaos] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [preservarModelos, setPreservarModelos] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [agindo, setAgindo] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [p, s, o] = await Promise.all([
        previaDaLimpeza(preservarModelos),
        resumoDasSeeds(),
        previaDeOrfaos(),
      ]);
      setPrevia(p.data);
      setSeeds(s.data);
      setOrfaos(o.data);
      setErro('');
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível carregar os controles.'));
    } finally {
      setCarregando(false);
    }
  }, [preservarModelos]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const rodarSeeds = async () => {
    setAgindo(true);
    setErro('');
    try {
      const { data } = await aplicarSeeds();
      const partes = [];
      if (data.criados.length) partes.push(`criados: ${data.criados.join(', ')}`);
      if (data.inativados.length)
        partes.push(`inativados: ${data.inativados.join(', ')}`);
      if (data.removidos.length)
        partes.push(`removidos: ${data.removidos.join(', ')}`);
      setAviso(
        data.sem_mudanca
          ? 'Seeds aplicadas — a base já estava igual aos arquivos versionados. Nada mudou.'
          : `Seeds aplicadas · ${partes.join(' · ')}`
      );
      await carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível aplicar as seeds.'));
    } finally {
      setAgindo(false);
    }
  };

  const varrerOrfaos = async () => {
    setAgindo(true);
    setErro('');
    try {
      const { data } = await limparOrfaos();
      setAviso(
        data.removidos
          ? `${numero(data.removidos)} arquivo(s) órfão(s) removidos do disco · ${mb(
              data.liberado_bytes
            )} liberados. Nenhum dado foi apagado.`
          : 'Nenhum arquivo órfão no disco — nada a fazer.'
      );
      await carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível varrer os arquivos órfãos.'));
    } finally {
      setAgindo(false);
    }
  };

  const confirmarLimpeza = async () => {
    setAgindo(true);
    setErro('');
    try {
      const { data } = await limparBase(preservarModelos);
      setConfirmando(false);
      setAviso(
        `Base limpa: ${numero(data.total)} registro(s) apagados em ${
          Object.keys(data.apagar).length
        } tabela(s).` +
          (preservarModelos ? ' Operadoras e modelos foram preservados.' : '')
      );
      await carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível limpar a base.'));
    } finally {
      setAgindo(false);
    }
  };

  if (carregando) return <LoadingSpinner texto="Carregando controles…" />;

  const linhas = Object.entries(previa?.apagar || {}).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <PageHeader
        titulo="Controles"
        subtitulo="Manutenção da base. Exclusivo do administrador."
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="ok" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      {/* ── Seeds ─────────────────────────────────────────────────────── */}
      <div className="mb-3">
        <Painel
          titulo="Modelos de leitura versionados (seeds)"
          subtitulo="Carrega para o banco as especificações que vivem em modelos-operadora/, no repositório."
          acoes={
            <Botao icone="bi-download" onClick={rodarSeeds} disabled={agindo}>
              {agindo ? 'Aplicando…' : 'Aplicar seeds'}
            </Botao>
          }
        >
          {!seeds?.modelos?.length ? (
            <Vazio
              icone="bi-file-earmark-code"
              titulo="Nenhum modelo versionado encontrado"
              descricao={
                seeds?.pasta_encontrada
                  ? 'A pasta modelos-operadora/ existe, mas está vazia.'
                  : 'A pasta modelos-operadora/ não foi encontrada a partir do backend.'
              }
            />
          ) : (
            <>
              <div className="ff-tabela-wrap">
                <Tabela>
                  <thead>
                    <tr>
                      <Th>Operadora</Th>
                      <Th>Modelo</Th>
                      <Th>Formato</Th>
                      <Th>Papel</Th>
                      <Th>O que vai acontecer</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {seeds.modelos.map((m) => {
                      const d = destinoDaSeed(m);
                      return (
                        <tr key={m.arquivo}>
                          <td className="col-meta">{m.operadora || '—'}</td>
                          <td>
                            {m.nome_modelo || m.arquivo}
                            <div className="ff-mono" style={{ fontSize: 11, color: 'var(--ff-muted)' }}>
                              {m.arquivo}
                            </div>
                          </td>
                          <td className="col-meta">{m.formato || '—'}</td>
                          <td className="col-meta">{m.papel || '—'}</td>
                          <td>
                            <Badge tom={d.tom}>{d.texto}</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Tabela>
              </div>
              <div className="p-3 pt-0" style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
                <i className="bi bi-shield-check me-2" aria-hidden="true" />
                Só é tocado o modelo de <strong>mesmo nome</strong>. Um modelo que você
                cadastrou pelo assistente, com outro nome, não é alterado nem desligado —
                mesmo que cubra o mesmo formato.
              </div>
            </>
          )}
        </Painel>
      </div>

      {/* ── Arquivos órfãos ───────────────────────────────────────────── */}
      <div className="mb-3">
        <Painel
          titulo="Arquivos órfãos no disco"
          subtitulo="Documentos que nenhuma tabela referencia mais — sobras de recebimentos excluídos."
          acoes={
            <Botao
              icone="bi-hdd"
              variante="secundario"
              onClick={varrerOrfaos}
              disabled={agindo || !orfaos?.removidos}
            >
              {agindo ? 'Varrendo…' : 'Limpar arquivos órfãos'}
            </Botao>
          }
        >
          <div className="p-3 d-flex flex-column gap-3">
            {orfaos?.removidos ? (
              <div className="ff-grid-tiles">
                <Indicador
                  rotulo="Órfãos"
                  valor={numero(orfaos.removidos)}
                  natureza="atencao"
                  nota={`de ${numero(orfaos.examinados)} arquivo(s) no diretório`}
                />
                <Indicador
                  rotulo="Espaço a liberar"
                  valor={mb(orfaos.liberado_bytes)}
                  nota="ocupado por documentos sem dono"
                />
              </div>
            ) : (
              <Vazio
                icone="bi-check-circle"
                titulo="Nenhum arquivo órfão"
                descricao={
                  orfaos
                    ? `Os ${numero(orfaos.examinados)} arquivos no diretório têm registro apontando para eles.`
                    : undefined
                }
              />
            )}

            <div style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
              <i className="bi bi-shield-check me-2" aria-hidden="true" />
              <strong>Só mexe no disco.</strong> Nenhuma linha de tabela é tocada —
              guias, valores, conciliação e auditoria continuam inteiros. Excluir um
              recebimento pela tela já tenta apagar o documento, mas a falha é só um
              aviso no log: o registro já foi embora e não faz sentido desfazer a
              exclusão por causa do disco. O que sobra desse caminho é o que esta
              varredura recolhe.
              <br />
              <i className="bi bi-clock-history me-2" aria-hidden="true" />
              Arquivo criado há menos de {orfaos?.horas ?? 6} horas não entra: poderia
              ser um envio em curso, cujo registro ainda não foi gravado. O
              arquivo-exemplo de cada modelo também fica — é ele que permite reconferir
              um mapeamento sem pedir o documento de volta.
            </div>
          </div>
        </Painel>
      </div>

      {/* ── Limpeza ───────────────────────────────────────────────────── */}
      <Painel
        titulo="Limpar a base"
        subtitulo="Apaga arquivos, extrações, conciliações e saldos. Usuários e auditoria ficam."
        acoes={
          <button
            type="button"
            className="ff-btn ff-btn--perigo"
            onClick={() => setConfirmando(true)}
            disabled={agindo || !previa?.total}
          >
            <i className="bi bi-trash3" aria-hidden="true" />
            Limpar base
          </button>
        }
      >
        <div className="p-3 d-flex flex-column gap-3">
          <div className="ff-aviso ff-aviso--atencao" style={{ margin: 0 }}>
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            <div>
              <strong>Não há como desfazer.</strong> Serve para recomeçar um teste do zero,
              sem o resíduo da tentativa anterior — o que antes era feito truncando tabela
              à mão no banco, e o que se faz à mão no banco não passa por auditoria nenhuma.
              Esta ação, sim, fica registrada na trilha.
            </div>
          </div>

          <label className="d-flex align-items-start gap-2" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              className="form-check-input mt-1"
              checked={preservarModelos}
              onChange={(e) => setPreservarModelos(e.target.checked)}
            />
            <span style={{ fontSize: 14 }}>
              Preservar operadoras e modelos de leitura
              <span className="d-block" style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                Sem isto, eles também são apagados. Os versionados voltam com
                “Aplicar seeds”; <strong>um modelo que você cadastrou pelo assistente não
                volta de lugar nenhum.</strong>
              </span>
            </span>
          </label>

          {!previa?.total ? (
            <Vazio
              icone="bi-check-circle"
              titulo="A base já está limpa"
              descricao="Não há nenhum registro a apagar além de usuários e auditoria."
            />
          ) : (
            <div className="ff-tabela-wrap">
              <Tabela estreita>
                <thead>
                  <tr>
                    <Th>Será apagado</Th>
                    <Th alinhar="right">Registros</Th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map(([t, n]) => (
                    <tr key={t}>
                      <td>
                        {nomeDe(t)}
                        <span className="ff-mono ms-2" style={{ fontSize: 11, color: 'var(--ff-muted)' }}>
                          {t}
                        </span>
                      </td>
                      <td className="col-qtd">{numero(n)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 600 }}>
                    <td>Total</td>
                    <td className="col-qtd">{numero(previa.total)}</td>
                  </tr>
                </tbody>
              </Tabela>
            </div>
          )}

          <div style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
            <i className="bi bi-lock me-2" aria-hidden="true" />
            Preservado:{' '}
            {Object.entries(previa?.preservar || {})
              .map(([t, n]) => `${nomeDe(t)} (${numero(n)})`)
              .join(' · ')}
          </div>
        </div>
      </Painel>

      {confirmando && (
        <ConfirmarLimpeza
          previa={previa}
          preservarModelos={preservarModelos}
          agindo={agindo}
          aoConfirmar={confirmarLimpeza}
          aoFechar={() => setConfirmando(false)}
        />
      )}
    </>
  );
}

/* A confirmação exige DIGITAR a palavra, não só clicar de novo.
   Um segundo clique no mesmo lugar é reflexo; digitar interrompe. E o que
   está em jogo aqui é tudo que já foi lançado. */
function ConfirmarLimpeza({ previa, preservarModelos, agindo, aoConfirmar, aoFechar }) {
  const [texto, setTexto] = useState('');
  const liberado = texto.trim().toUpperCase() === 'LIMPAR';

  return (
    <>
      <div className="modal-backdrop fade show" style={{ background: 'rgba(16,24,40,.5)' }} />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div
            className="modal-content"
            style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
          >
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <div>
                <h2 className="ff-painel__titulo">Limpar a base</h2>
                <p className="ff-painel__sub">
                  {numero(previa.total)} registro(s) em{' '}
                  {Object.keys(previa.apagar).length} tabela(s)
                </p>
              </div>
              <button type="button" className="btn-close" onClick={aoFechar} aria-label="Fechar" />
            </div>

            <div className="modal-body d-flex flex-column gap-3">
              <div className="ff-aviso ff-aviso--erro" style={{ margin: 0 }}>
                <i className="bi bi-exclamation-octagon" aria-hidden="true" />
                <div>
                  Somem arquivos recebidos, linhas extraídas, guias, glosas, pendências,
                  divergências, saldos e os vínculos guia → protocolo.{' '}
                  {preservarModelos
                    ? 'Operadoras e modelos de leitura ficam.'
                    : 'Operadoras e modelos de leitura também somem.'}{' '}
                  <strong>Usuários e auditoria permanecem.</strong>
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirmar-limpar"
                  style={{ fontSize: 12, color: 'var(--ff-muted)' }}
                >
                  Para confirmar, digite <strong>LIMPAR</strong>
                </label>
                <input
                  id="confirmar-limpar"
                  className="ff-input ff-mono"
                  value={texto}
                  autoComplete="off"
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="LIMPAR"
                />
              </div>
            </div>

            <div className="modal-footer" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <Botao variante="secundario" onClick={aoFechar} disabled={agindo}>
                Cancelar
              </Botao>
              <button
                type="button"
                className="ff-btn ff-btn--perigo"
                onClick={aoConfirmar}
                disabled={agindo || !liberado}
              >
                {agindo ? 'Limpando…' : 'Limpar definitivamente'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
